import React, {PropsWithChildren, ReactElement, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IElementQuestion} from '../../../../../incase-domain/elements/question/question';
import {fetchQuestionReportAction} from '../../../../../store/actions/analytics.actions';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {QuestionElementInteractions, IRunFile} from '../../../../../incase-domain/proptypes/runfile';
import {pointsSelector} from '../../../../../store/selectors/runfile.selectors';
import QuestionLayout from '../common/QuestionLayout';
import {useQuestionRemainingToast} from '../common/useQuestionRemainingToast';
import {gameConfigSelector, loadedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {handleRetryAndResultOnClick} from '../utils/handleClick.question.utils';
import {handleSelectAnswer} from '../utils/handleTryButton.question.utils';
import QuestionAnswer from './QuestionAnswer';
import {getAnswerStyles} from '../common/getStyles';
import {getAnswerType} from '../common/utils';
import useQuestionTry from '../useQuestionTry';
import {useOpenComment} from '../common/useOpenComment';
import {useSlidePreviewCapture} from '../../../../../context/SlidePreviewCaptureContext';

interface QuestionElementProps {
    element: IElementQuestion;
    savePresentationRunFile: (elementRunFile: QuestionElementInteractions, newPoints?: number) => void;
    device?: DeviceType;
    elementInteractions: QuestionElementInteractions;
}

function QuestionElement({
    element,
    elementInteractions,
    savePresentationRunFile,
    device = DeviceType.DESKTOP,
}: PropsWithChildren<QuestionElementProps>): ReactElement {
    const dispatch = useDispatch();

    const presentation = useSelector(loadedPresentationSelector);
    const points = useSelector(pointsSelector);
    const gameConfig = useSelector(gameConfigSelector);

    const {emitRemainingTriesToast} = useQuestionRemainingToast();
    const handleTry = useQuestionTry<IElementQuestion>();

    const {getCommentProps, toggleComment} = useOpenComment();
    const isSlidePreviewCapture = useSlidePreviewCapture();

    const {wrongAnswer} = elementInteractions;

    const clickedAnswers = elementInteractions.clickedAnswers ?? [];
    const markedAnswers = elementInteractions.markedAnswers ?? [];
    const {answered} = elementInteractions;

    useEffect(() => {
        if (element.showResults) {
            dispatch(fetchQuestionReportAction(element.id));
        }
    }, [dispatch, element.id, element.showResults, answered]);

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        if (wrongAnswer !== undefined) {
            timeout = setTimeout(() => {
                savePresentationRunFile({...elementInteractions, wrongAnswer: undefined});
            }, 1000);
        }
        return () => clearTimeout(timeout);
    }, [wrongAnswer, elementInteractions, savePresentationRunFile]);

    function handleAnswerClick(index: number): void {
        if (element.haveTryButton) {
            handleSelectAnswer(elementInteractions, element, index, markedAnswers, savePresentationRunFile, presentation);
        } else {
            handleRetryAndResultOnClick(
                element,
                clickedAnswers,
                index,
                elementInteractions,
                {points} as IRunFile,
                gameConfig,
                savePresentationRunFile,
                emitRemainingTriesToast,
                dispatch,
                presentation
            );
        }
    }

    const isAnswerSelected = (index: number): boolean =>
        !!elementInteractions.markedAnswers?.includes(index) || !!elementInteractions.clickedAnswers?.includes(index);

    return (
        <QuestionLayout
            element={element}
            elementInteractions={elementInteractions}
            device={device}
            showConfirmButton
            onConfirm={() => handleTry(element, elementInteractions)}
            confirmDisabled={markedAnswers.length === 0}
        >
            <div className={`c-questionElement ${device === DeviceType.MOBILE ? 'c-questionElement--mobile' : ''}`}>
                {element.answers.map((answer, index) => (
                    <QuestionAnswer
                        key={`question-answer-${element.id}-${index}`}
                        answer={answer.text}
                        answerType={getAnswerType(answer, answered)}
                        comment={
                            isSlidePreviewCapture || (elementInteractions.answered && (isAnswerSelected(index) || element.showAllComments))
                                ? answer.comment
                                : undefined
                        }
                        leadingIcon={isAnswerSelected(index) ? 'solar:check-square-bold' : 'solar:stop-linear'}
                        correctColor={element.correctColor}
                        incorrectColor={element.incorrectColor}
                        debatableColor={element.debatableColor}
                        styles={getAnswerStyles(device, element)}
                        onClick={() => {
                            handleAnswerClick(index);
                            toggleComment(
                                index,
                                !!answer.comment && elementInteractions.answered && (isAnswerSelected(index) || element.showAllComments)
                            );
                        }}
                        {...getCommentProps(
                            index,
                            !!answer.comment && elementInteractions.answered && (isAnswerSelected(index) || element.showAllComments)
                        )}
                        writeAnswerIndices={element.writeAnswerIndices}
                        index={index}
                        device={device}
                    />
                ))}
            </div>
        </QuestionLayout>
    );
}

export default QuestionElement;
